import { store_product_path } from "@/lib/routes";
import { AdminPageProps, Product } from "@/types";
import { useForm, usePage } from "@inertiajs/react";
import { FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Field, FieldLabel, FieldSet } from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { getCurrencySymbol } from "@/lib/utils";
import { SheetProps } from "@/types/ui";
import { Textarea } from "@/components/ui/textarea";
import useTranslations from "@/hooks/use-translations";

type ProductFormData = {
  product: Partial<Product> & {
    custom_image: File | string | null;
  };
};

export default function EditProductSheet({
  product,
  open,
  onOpenChange,
}: {
  product: Product;
} & SheetProps) {
  const editProductForm = useForm<ProductFormData>({
    product: {
      product_type: product.product_type,
      system_code: product.system_code,
      state: product.state,
      price: product.price_cents / 100,
      stock: product.stock,
      price_cents: product.price_cents,
      description: product.description,
      custom_image: product.custom_image_signed_id,
    },
  });
  const { data, setData } = editProductForm;
  const { current_store } = usePage<AdminPageProps>().props;
  const { t } = useTranslations("shared.components.edit_product_sheet");

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    editProductForm.patch(store_product_path(current_store!.id, product.id), {
      invalidateCacheTags: ["stores", current_store!.id ?? ""],
      onSuccess() {
        editProductForm.resetAndClearErrors();
        onOpenChange(false);
      },
    });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{t("title", { name: product.name })}</SheetTitle>
        </SheetHeader>

        <form
          id={`edit-product-form-${product.id}`}
          onSubmit={onSubmit}
          className="flex-1 flex flex-col gap-5 px-4 overflow-auto"
        >
          <FieldSet>
            <Field>
              <FieldLabel htmlFor="state">{t("shared.fields.state")}</FieldLabel>
              <Select
                name="product[state]"
                defaultValue="new_sealed"
                value={data.product.state}
                onValueChange={(value) => setData("product.state", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("shared.fields.state_placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_sealed">
                    {t("shared.fields.state_new_sealed")}
                  </SelectItem>
                  <SelectItem value="new_complete">
                    {t("shared.fields.state_new")}
                  </SelectItem>
                  <SelectItem value="pre_owned">
                    {t("shared.fields.state_pre_owned")}
                  </SelectItem>
                  <SelectItem value="pre_owned_with_box">
                    {t("shared.fields.state_pre_owned_with_box")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field>
              <FieldLabel htmlFor="price">{t("shared.fields.price")}</FieldLabel>

              <InputGroup>
                <InputGroupAddon>
                  <InputGroupText>
                    {getCurrencySymbol(current_store?.currency ?? "usd")}
                  </InputGroupText>
                </InputGroupAddon>
                <InputGroupInput
                  id="price"
                  name="product[price]"
                  type="number"
                  step="0.01"
                  placeholder={t("shared.fields.price_placeholder")}
                  value={data.product.price}
                  onChange={(e) =>
                    setData((values) => ({
                      ...values,
                      product: {
                        ...values.product,
                        price: e.target.valueAsNumber,
                        price_cents: e.target.valueAsNumber * 100,
                      },
                    }))
                  }
                />
                <InputGroupAddon align="inline-end">
                  <InputGroupText>
                    {current_store?.currency.toUpperCase()}
                  </InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            </Field>

            <Field>
              <FieldLabel htmlFor="stock">{t("shared.fields.stock")}</FieldLabel>
              <Input
                id="stock"
                name="product[stock]"
                type="number"
                step="1"
                placeholder={t("shared.fields.stock_placeholder")}
                value={data.product.stock}
                onChange={(e) =>
                  setData("product.stock", e.target.valueAsNumber)
                }
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="description">
                {t("description_label")}
              </FieldLabel>
              <Textarea
                id="description"
                name="product[description]"
                placeholder={t("description_placeholder")}
                value={data.product.description}
                onChange={(e) => setData("product.description", e.target.value)}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="custom_image">
                {t("custom_image_label")}
              </FieldLabel>
              <Input
                id="custom_image"
                name="product[custom_image]"
                type="file"
                onChange={(event) =>
                  event.target.files &&
                  setData("product.custom_image", event.target.files[0])
                }
              />
            </Field>
          </FieldSet>
        </form>

        <SheetFooter>
          <Button
            type="submit"
            form={`edit-product-form-${product.id}`}
            disabled={editProductForm.processing}
          >
            {editProductForm.processing && <Spinner />}
            {t("shared.actions.save")}
          </Button>
          <SheetClose asChild>
            <Button variant="outline" disabled={editProductForm.processing}>
              {t("shared.actions.close")}
            </Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
